import React from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Mail,
  ChevronRight,
  Linkedin,
  Twitter,
  Facebook,
  Instagram,
} from "lucide-react";
import WhatsAppIcon from "./WhatsAppIcon";
import { companyInfo } from "../data/mock";

const Footer = () => {
  const learnLinks = [
    { label: "Home", to: "/" },
    { label: "About", to: "/about" },
    { label: "Services", to: "/services" },
    { label: "Special Services", to: "/special-services" },
    { label: "Handbook & Reference", to: "/handbook" },
    { label: "Request Info", to: "/request-info" },
    { label: "Blog", to: "/blog" },
    { label: "Contact", to: "/contact" },
  ];

  const refLinks = [
    "Licensed & Insured Contractor",
    "Quality Assurance Standards",
    "Service Terms & Conditions",
    "Privacy Policy",
  ];

  return (
    <footer className="relative overflow-hidden" style={{ backgroundColor: "#061b13" }}>
      {/* Decorative blob */}
      <div
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse, #10b981 0%, transparent 70%)",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo + tagline */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-white text-lg shadow-lg shadow-emerald-500/30"
                style={{
                  background:
                    "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                }}
              >
                DH
              </div>
              <div>
                <div className="font-bold text-lg text-white">
                  D-H Gadiola
                </div>
                <div className="text-xs text-emerald-200/70 uppercase tracking-[0.2em]">
                  Cable Specialist
                </div>
              </div>
            </div>
            <p className="text-emerald-100/70 text-sm leading-relaxed">
              Independent contractor specializing in design, engineering and
              installation of electrical wire & cable solutions across Hawaii.
            </p>
          </div>

          {/* Learn More */}
          <div>
            <h4 className="text-base font-bold mb-5 text-white tracking-tight">
              Learn More
              <div className="w-10 h-[3px] mt-2 rounded-full" style={{ background: "linear-gradient(90deg, #10b981, transparent)" }} />
            </h4>
            <ul className="space-y-2.5">
              {learnLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="flex items-center gap-1.5 text-sm text-emerald-100/70 hover:text-emerald-300 transition-colors"
                  >
                    <ChevronRight size={13} className="text-emerald-400" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Us */}
          <div>
            <h4 className="text-base font-bold mb-5 text-white tracking-tight">
              Contact Us
              <div className="w-10 h-[3px] mt-2 rounded-full" style={{ background: "linear-gradient(90deg, #10b981, transparent)" }} />
            </h4>
            <ul className="space-y-4 text-sm text-emerald-100/80">
              <li className="flex items-start gap-3">
                <MapPin size={17} className="text-emerald-400 mt-0.5 shrink-0" />
                <span>{companyInfo.address}</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={17} className="text-emerald-400 mt-0.5 shrink-0" />
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="hover:text-emerald-300 transition-colors break-all"
                >
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <WhatsAppIcon size={17} className="text-emerald-400 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-white">WhatsApp</div>
                  <a
                    href={companyInfo.whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-emerald-300 transition-colors"
                  >
                    {companyInfo.whatsapp}
                  </a>
                </div>
              </li>
            </ul>

            {/* Social */}
            <div className="flex items-center gap-2 mt-6">
              {[Linkedin, Twitter, Facebook, Instagram].map((Icon, idx) => (
                <a
                  key={idx}
                  href="#"
                  className="w-9 h-9 rounded-full bg-white/5 border border-emerald-400/20 text-emerald-300 flex items-center justify-center hover:bg-emerald-500 hover:border-emerald-500 hover:text-white transition-colors"
                  aria-label="social"
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>

          {/* Reference */}
          <div>
            <h4 className="text-base font-bold mb-5 text-white tracking-tight">
              Reference Documents
              <div className="w-10 h-[3px] mt-2 rounded-full" style={{ background: "linear-gradient(90deg, #10b981, transparent)" }} />
            </h4>
            <ul className="space-y-2.5">
              {refLinks.map((label) => (
                <li key={label}>
                  <a
                    href="#"
                    className="flex items-center gap-1.5 text-sm text-emerald-100/70 hover:text-emerald-300 transition-colors"
                  >
                    <ChevronRight size={13} className="text-emerald-400" />
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative border-t border-white/10">
        <div className="max-w-[1280px] mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-emerald-100/50">
            © {new Date().getFullYear()} D-H Gadiola Independent Contractor &
            Cable Specialist. All Rights Reserved.
          </p>
          <p className="text-xs text-emerald-100/50">
            Honolulu, Hawaii — Serving the Pacific Region
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
